/**
 * ExecutionTree Component
 *
 * Displays execution events in a hierarchical tree structure:
 * - Execution (root)
 *   - Planning
 *   - Steps
 *     - Tool calls
 *     - LLM calls
 *     - Thinking
 */

import { Component, createMemo, createSignal, For, Show } from 'solid-js';
import type { ExecutionEvent, ExecutionEventType } from '../../services/api.ts';
import { getAgentColorById } from './AgentFilter.tsx';
import type { AgentInfo } from '../../stores/executionStore.ts';
import JsonViewer from './JsonViewer.tsx';

interface ExecutionTreeProps {
  events: ExecutionEvent[];
  agents: AgentInfo[];
  formatTime: (timestamp: string) => string;
  formatDuration: (ms?: number) => string;
}

// Node types for the tree
interface TreeNode {
  id: string;
  type: 'execution' | 'planning' | 'step' | 'tool' | 'llm' | 'thinking' | 'message';
  label: string;
  sublabel?: string;
  status?: 'running' | 'completed' | 'failed';
  event: ExecutionEvent;
  children: TreeNode[];
  agentId?: string;
  agentName?: string;
  model?: string;
  durationMs?: number;
  tokens?: number;
}

// Build hierarchical tree from flat events
function buildExecutionTree(events: ExecutionEvent[]): TreeNode[] {
  if (events.length === 0) return [];

  const roots: TreeNode[] = [];
  const stepNodes = new Map<string, TreeNode>();
  let currentPlanningNode: TreeNode | null = null;
  let currentStepNode: TreeNode | null = null;

  for (const event of events) {
    const node = createNodeFromEvent(event);
    if (!node) continue;

    // Handle execution-level events
    if (event.eventType === 'execution_started') {
      roots.push(node);
      continue;
    }

    if (event.eventType === 'execution_completed' || event.eventType === 'execution_failed' || event.eventType === 'execution_cancelled') {
      roots.push(node);
      continue;
    }

    // Handle planning
    if (event.eventType === 'planning_started') {
      currentPlanningNode = node;
      roots.push(node);
      continue;
    }

    if (event.eventType === 'planning_completed') {
      if (currentPlanningNode) {
        currentPlanningNode.status = 'completed';
        currentPlanningNode.children.push(node);
      } else {
        roots.push(node);
      }
      currentPlanningNode = null;
      continue;
    }

    // Handle steps
    if (event.eventType === 'step_started') {
      const stepId = (event.payload as { stepId?: string })?.stepId || event.id;
      currentStepNode = node;
      stepNodes.set(stepId, node);
      roots.push(node);
      continue;
    }

    if (event.eventType === 'step_completed' || event.eventType === 'step_failed') {
      const stepId = (event.payload as { stepId?: string })?.stepId;
      if (stepId && stepNodes.has(stepId)) {
        const stepNode = stepNodes.get(stepId)!;
        stepNode.status = event.eventType === 'step_completed' ? 'completed' : 'failed';
        stepNode.children.push(node);
      } else if (currentStepNode) {
        currentStepNode.status = event.eventType === 'step_completed' ? 'completed' : 'failed';
        currentStepNode.children.push(node);
      } else {
        roots.push(node);
      }
      currentStepNode = null;
      continue;
    }

    // Tool calls, LLM calls, thinking - attach to current step
    if (
      event.eventType === 'tool_call_started' ||
      event.eventType === 'tool_call_completed' ||
      event.eventType === 'tool_call_failed' ||
      event.eventType === 'llm_call_started' ||
      event.eventType === 'llm_call_completed' ||
      event.eventType === 'thinking_started' ||
      event.eventType === 'thinking_completed'
    ) {
      if (currentStepNode) {
        currentStepNode.children.push(node);
      } else if (currentPlanningNode) {
        currentPlanningNode.children.push(node);
      } else {
        roots.push(node);
      }
      continue;
    }

    // Messages and progress updates - add to roots
    roots.push(node);
  }

  return roots;
}

function createNodeFromEvent(event: ExecutionEvent): TreeNode | null {
  const payload = event.payload as Record<string, unknown>;

  const base = {
    id: event.id,
    event,
    children: [],
    agentId: event.agentId,
    agentName: event.agentName,
    durationMs: event.durationMs,
  };

  switch (event.eventType) {
    case 'execution_started':
      return { ...base, type: 'execution', label: 'Execution Started', status: 'running' };
    case 'execution_completed':
      return { ...base, type: 'execution', label: 'Execution Completed', status: 'completed' };
    case 'execution_failed':
      return { ...base, type: 'execution', label: 'Execution Failed', status: 'failed', sublabel: payload?.error as string };
    case 'execution_cancelled':
      return { ...base, type: 'execution', label: 'Execution Cancelled', status: 'failed' };

    case 'planning_started':
      return { ...base, type: 'planning', label: 'Planning', status: 'running' };
    case 'planning_completed':
      const steps = (payload?.steps as unknown[]) || [];
      return { ...base, type: 'planning', label: 'Plan Created', sublabel: `${steps.length} steps`, status: 'completed' };

    case 'step_started':
      return { ...base, type: 'step', label: payload?.description as string || 'Step', status: 'running' };
    case 'step_completed':
      return { ...base, type: 'step', label: 'Step Completed', status: 'completed' };
    case 'step_failed':
      return { ...base, type: 'step', label: 'Step Failed', status: 'failed', sublabel: payload?.error as string };

    case 'tool_call_started':
      return { ...base, type: 'tool', label: `Tool: ${payload?.toolName || 'unknown'}`, status: 'running' };
    case 'tool_call_completed':
      return { ...base, type: 'tool', label: `Tool: ${payload?.toolName || 'unknown'}`, status: 'completed' };
    case 'tool_call_failed':
      return { ...base, type: 'tool', label: `Tool: ${payload?.toolName || 'unknown'}`, status: 'failed', sublabel: payload?.error as string };

    case 'llm_call_started':
      return {
        ...base,
        type: 'llm',
        label: 'LLM Call',
        model: payload?.model as string,
        status: 'running'
      };
    case 'llm_call_completed':
      return {
        ...base,
        type: 'llm',
        label: 'LLM Response',
        model: payload?.model as string,
        tokens: payload?.totalTokens as number,
        status: 'completed'
      };

    case 'thinking_started':
      return { ...base, type: 'thinking', label: 'Thinking', status: 'running' };
    case 'thinking_completed':
      const content = payload?.content as string || '';
      return { ...base, type: 'thinking', label: 'Thought', sublabel: content.slice(0, 100) + (content.length > 100 ? '...' : ''), status: 'completed' };

    case 'agent_message':
      const level = payload?.level as string || 'info';
      const message = payload?.message as string || '';
      return { ...base, type: 'message', label: `Message (${level})`, sublabel: message.slice(0, 100) + (message.length > 100 ? '...' : '') };

    case 'progress_update':
      return { ...base, type: 'message', label: 'Progress Update', sublabel: payload?.message as string };

    default:
      return { ...base, type: 'message', label: event.eventType };
  }
}

// Icons for different node types
const NodeIcon: Component<{ type: TreeNode['type']; status?: TreeNode['status'] }> = (props) => {
  const baseClass = 'w-4 h-4 flex-shrink-0';

  const color = () => {
    if (props.status === 'failed') return 'text-red-400';
    if (props.status === 'running') return 'text-cyan-400';
    if (props.status === 'completed') return 'text-green-400';
    return 'text-gray-400';
  };

  switch (props.type) {
    case 'execution':
      return (
        <svg class={`${baseClass} ${color()}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'planning':
      return (
        <svg class={`${baseClass} ${color()}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      );
    case 'step':
      return (
        <svg class={`${baseClass} ${color()}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'tool':
      return (
        <svg class={`${baseClass} ${color()}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case 'llm':
      return (
        <svg class={`${baseClass} ${color()}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      );
    case 'thinking':
      return (
        <svg class={`${baseClass} ${color()}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      );
    case 'message':
      return (
        <svg class={`${baseClass} ${color()}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      );
    default:
      return <div class={`${baseClass} rounded-full bg-gray-600`} />;
  }
};

// Tree node component
interface TreeNodeViewProps {
  node: TreeNode;
  depth: number;
  agents: AgentInfo[];
  formatTime: (timestamp: string) => string;
  formatDuration: (ms?: number) => string;
}

const TreeNodeView: Component<TreeNodeViewProps> = (props) => {
  const [expanded, setExpanded] = createSignal(props.depth < 2);
  const [showPayload, setShowPayload] = createSignal(false);

  const hasChildren = () => props.node.children.length > 0;
  const hasPayload = () => props.node.event.payload && Object.keys(props.node.event.payload as object).length > 0;

  const agentColor = () => {
    if (!props.node.agentId) return null;
    return getAgentColorById(props.node.agentId, props.agents);
  };

  return (
    <div class="relative">
      {/* Vertical line connecting siblings */}
      <Show when={props.depth > 0}>
        <div class="absolute left-3 top-0 bottom-0 w-px bg-gray-700" />
      </Show>

      {/* Node row */}
      <div
        class={`
          group flex items-start gap-2 py-2 px-2 rounded-lg transition-colors
          ${hasChildren() || hasPayload() ? 'cursor-pointer hover:bg-gray-800/50' : ''}
        `}
        onClick={() => {
          if (hasChildren()) {
            setExpanded(!expanded());
          } else if (hasPayload()) {
            setShowPayload(!showPayload());
          }
        }}
      >
        {/* Expand/collapse button */}
        <div class="flex-shrink-0 w-5 h-5 flex items-center justify-center">
          <Show when={hasChildren()}>
            <svg
              class={`w-4 h-4 text-gray-500 transform transition-transform ${expanded() ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </Show>
          <Show when={!hasChildren() && hasPayload()}>
            <svg
              class={`w-3 h-3 text-gray-600 transform transition-transform ${showPayload() ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </Show>
        </div>

        {/* Icon */}
        <NodeIcon type={props.node.type} status={props.node.status} />

        {/* Content */}
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="font-medium text-gray-200">{props.node.label}</span>

            {/* Agent badge */}
            <Show when={props.node.agentName && agentColor()}>
              <span class={`text-xs px-2 py-0.5 rounded-full ${agentColor()!.bg} ${agentColor()!.text} ${agentColor()!.border} border`}>
                {props.node.agentName}
              </span>
            </Show>

            {/* Model badge */}
            <Show when={props.node.model}>
              <span class="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-300 border border-green-500/40">
                {props.node.model}
              </span>
            </Show>

            {/* Tokens */}
            <Show when={props.node.tokens}>
              <span class="text-xs text-gray-500">{props.node.tokens?.toLocaleString()} tokens</span>
            </Show>

            {/* Running indicator */}
            <Show when={props.node.status === 'running'}>
              <span class="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            </Show>
          </div>

          {/* Sublabel */}
          <Show when={props.node.sublabel}>
            <p class="text-sm text-gray-400 line-clamp-2">{props.node.sublabel}</p>
          </Show>
        </div>

        {/* Metadata */}
        <div class="flex-shrink-0 text-right">
          <div class="text-xs text-gray-500">{props.formatTime(props.node.event.timestamp)}</div>
          <Show when={props.node.durationMs}>
            <div class="text-xs text-gray-600">{props.formatDuration(props.node.durationMs)}</div>
          </Show>
        </div>
      </div>

      {/* Payload expansion (for leaf nodes) */}
      <Show when={showPayload() && hasPayload() && !hasChildren()}>
        <div class="ml-12 mr-4 mb-2 p-3 rounded-lg bg-gray-900/50 border border-gray-700">
          <JsonViewer data={props.node.event.payload} expanded={true} />
        </div>
      </Show>

      {/* Children */}
      <Show when={expanded() && hasChildren()}>
        <div class="ml-6">
          <For each={props.node.children}>
            {(child) => (
              <TreeNodeView
                node={child}
                depth={props.depth + 1}
                agents={props.agents}
                formatTime={props.formatTime}
                formatDuration={props.formatDuration}
              />
            )}
          </For>
        </div>
      </Show>
    </div>
  );
};

const ExecutionTree: Component<ExecutionTreeProps> = (props) => {
  const tree = createMemo(() => buildExecutionTree(props.events));

  return (
    <div class="space-y-1">
      <Show when={tree().length === 0}>
        <div class="p-8 text-center text-gray-500">
          No events to display
        </div>
      </Show>

      <For each={tree()}>
        {(node) => (
          <TreeNodeView
            node={node}
            depth={0}
            agents={props.agents}
            formatTime={props.formatTime}
            formatDuration={props.formatDuration}
          />
        )}
      </For>
    </div>
  );
};

export default ExecutionTree;
